/* eslint-disable no-unused-vars */
const Autodesk = window.Autodesk;
import store from '@/store';
import { removeFromScene, pointerToRaycaster } from '../utils/forgeUtils'
import { createForgeMaterial, getBoxControlArrows, drawBoxLines } from '../utils/threejsUtils'
import { ForgeScaffold, isScaffoldPlaced } from './../scripts/js/forgeModels';
import { TurnViewerLockOff } from './../scripts/js/forgeviewer';
import { log as debugLog } from '@/utils/debugLogger';
const THREE = window.THREE;

// A scaffold box is drawable only when every rendered extent (max x scale) is a
// positive finite number. Dimensions come from nullable columns and unvalidated
// input, and a non-finite extent makes THREE emit NaN vertices into the shared
// overlay scene.
const isDrawableExtent = (extent) => Number.isFinite(extent) && extent > 0;

class ScaffoldBoxExtension extends Autodesk.Viewing.Extension {
    constructor(loadedViewer, options) {
        super(loadedViewer, options);
        this.viewer = loadedViewer;
        this.webGLDOM = this.viewer.canvas.parentElement.parentElement.parentElement;
        this.sceneName = 'potree-scene';
        this.controlBoxColor = 0x5A002A;
        this.cameraPosition = new THREE.Vector3();

        this.boxesMeshes = [];
        this.controlBox = null;
        this.controlBoxMesh = null;

        this.draggingPoint = null;
        this.highlightedTriangles = null;

        this.controlPoints = [];

        // Coloring map is used to store the custom color of the boxes
        this.coloringMap = {};
        this.isolatedIds = [];

        this.updateTimeout = null;
    }

    load() {
        this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, (e) => {
            var cameraPosition = this.viewer?.navigation?.getPosition();

            if (!this.isCameraPositionChanged(cameraPosition)) return;

            this.cameraPosition = cameraPosition.clone();

            this.updateBoxControls();
        });

        this.webGLDOM.addEventListener("click", (event) => {
            this.onMouseUp(event);
        });
        this.viewer.canvas.addEventListener("mousedown", (event) => {
            this.onMouseDown(event);
        });
        this.webGLDOM.addEventListener("mousemove", (event) => {
            this.onMouseMove(event);
        }, false);
        return true;
    }
    unload() {
        this.clearScreen();

        this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, () => {
            this.updateBoxControls()
        });
        this.webGLDOM.removeEventListener("click", (event) => {
            this.onMouseUp(event);
        });
        this.viewer.canvas.removeEventListener("mousedown", (event) => {
            this.onMouseDown(event);
        });
        this.webGLDOM.removeEventListener("mousemove", (event) => {
            this.onMouseMove(event);
        }, false);

        this.viewer.impl.invalidate(true, true, true);

        return true;
    }
    debouncedUpdate(timeout = 100) {
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
        }
        this.updateTimeout = setTimeout(() => {
            this.update();
        }, timeout);
    }
    update() {
        this.clearScreen();

        let isGlobalSearchTabShown = store.state.isTabShown && store.state.selectedTab == 0;

        let visibleScaffolds = [];

        if (store.state.userSettings.displaySettings.showScaffolds) {
            let scaffolds = (isGlobalSearchTabShown && store.getters['GET_GLOBAL_SEARCH_TEXT']) ? store.state.filteredScaffolds : store.state.scaffolds.filter(o => o.isScaffold())

            visibleScaffolds = [...visibleScaffolds, ...scaffolds];
        }

        if (store.state.userSettings.displaySettings.showPermits) {
            let permits = (isGlobalSearchTabShown && store.getters['GET_GLOBAL_SEARCH_TEXT']) ? store.state.filteredPermits : store.state.scaffolds.filter(o => o.isPermit())

            visibleScaffolds = [...visibleScaffolds, ...permits];
        }

        if (store.state.userSettings.displaySettings.showDeckManagment) {
            let containers = (isGlobalSearchTabShown && store.getters['GET_GLOBAL_SEARCH_TEXT']) ? store.state.filteredContainers : store.state.scaffolds.filter(o => o.isContainer())

            visibleScaffolds = [...visibleScaffolds, ...containers];
        }

        // An unplaced tag has no box to draw: a register-created or cleared one sits at the
        // origin, where a 2m box is even more visible than a stray icon (AVA-492). Applied to
        // the assembled list so it covers the global-search branches above as well.
        visibleScaffolds = visibleScaffolds.filter(isScaffoldPlaced);

        if (this.isolatedIds.length) {
            visibleScaffolds = visibleScaffolds.filter(o => this.isolatedIds.includes(o.id));
        }

        visibleScaffolds = store.state.forgeService.updateListWithEditingTag(visibleScaffolds) ?? [];

        if (visibleScaffolds.length) {
            if (!this.viewer.overlays.hasScene(this.sceneName)) {
                this.viewer.overlays.addScene(this.sceneName);
            }

            this.createScaffoldsMeshes(visibleScaffolds);
        }
        return true;
    }
    isCameraPositionChanged(cameraPosition) {
        let unitScale = store.state.unitScale ?? 1;
        return (cameraPosition && this.cameraPosition && cameraPosition.distanceTo(this.cameraPosition) > unitScale)
    }
    setIsolation(ids) {
        this.isolatedIds = ids;
        this.debouncedUpdate();
    }
    clearIsolation() {
        this.isolatedIds = [];
        this.debouncedUpdate();
    }
    setCustomColor(id, colorHex) {
        this.coloringMap[id] = colorHex;
        this.debouncedUpdate();
    }
    resetColoring() {
        if (Object.keys(this.coloringMap).length) {
            this.coloringMap = {};
            this.debouncedUpdate(500);
        }
    }
    clearScreen() {
        if (this.boxesMeshes?.length) {
            removeFromScene(this.boxesMeshes, this.viewer, this.sceneName);
            this.boxesMeshes = [];
        }
        if (this.controlBoxMesh) {
            removeFromScene(this.controlBoxMesh, this.viewer, this.sceneName);
            this.controlBoxMesh = null;
            this.controlBox = null;
        }
        if (this.controlPoints?.length) {
            removeFromScene(this.controlPoints.map(o => o.arrow.arrow), this.viewer, this.sceneName);
            removeFromScene(this.controlPoints.map(o => o.arrow.cylinder), this.viewer, this.sceneName);
            this.controlPoints = [];
        }
    }
    createScaffoldsMeshes(visibleScaffolds) {
        let selectedScaffold = store.getters['GET_EDITING_TAG'];
        let isEditing = !store.getters['GET_IS_TAG_READ_ONLY']

        for (let i = 0; i < visibleScaffolds.length; i++) {
            const scaffold = visibleScaffolds[i];

            let x = store.state.forgeService.worldToView3dLength(scaffold.max.x)
            let y = store.state.forgeService.worldToView3dLength(scaffold.max.y)
            let z = store.state.forgeService.worldToView3dLength(scaffold.max.z)

            const extents = [x * scaffold.scale.x, y * scaffold.scale.y, z * scaffold.scale.z];

            if (!extents.every(isDrawableExtent)) {
                debugLog('[ScaffoldBoxExtension] Skipping degenerate box for tag', scaffold.id, {
                    name: scaffold.name,
                    max: { x: scaffold.max.x, y: scaffold.max.y, z: scaffold.max.z },
                    scale: { x: scaffold.scale.x, y: scaffold.scale.y, z: scaffold.scale.z },
                    viewExtents: extents,
                });
                continue;
            }

            const geometry = new THREE.BufferGeometry().fromGeometry(new THREE.BoxGeometry(x, y, z));
            const material = createForgeMaterial(this.viewer, this.coloringMap[scaffold.id] ?? scaffold.getForgeColor(), 0.4, true);
            const boxMesh = new THREE.Mesh(geometry, material);

            boxMesh.guid = scaffold.id;
            boxMesh.name = scaffold.name;
            boxMesh.position.x = scaffold.viewPosition.x;
            boxMesh.position.y = scaffold.viewPosition.y;
            boxMesh.position.z = scaffold.viewPosition.z;
            boxMesh.scale.x = scaffold.scale.x;
            boxMesh.scale.y = scaffold.scale.y;
            boxMesh.scale.z = scaffold.scale.z;

            this.boxesMeshes.push(boxMesh);

            if (isEditing && scaffold.id == selectedScaffold?.id) {
                this.controlBox = new THREE.Box3().setFromObject(boxMesh);
                this.updateBoxControls();
            } else {
                this.viewer.overlays.addMesh(boxMesh, this.sceneName);
            }
        }

        this.viewer.impl.invalidate(true, true, true);
    }
    updateBoxControls() {
        if (!this.controlBox) return;

        //cleared previous drawing
        if (this.controlBoxMesh) {
            removeFromScene(this.controlBoxMesh, this.viewer, this.sceneName);
        }
        if (this.controlPoints?.length) {
            removeFromScene(this.controlPoints.map(o => o.arrow.arrow), this.viewer, this.sceneName);
            removeFromScene(this.controlPoints.map(o => o.arrow.cylinder), this.viewer, this.sceneName);
        }

        //draw new
        this.controlBoxMesh = drawBoxLines(this.controlBox, this.controlBoxColor, this.viewer, this.sceneName);

        this.controlPoints = getBoxControlArrows(this.controlBox, this.viewer, this.sceneName, 0.5)
    }
    checkAndHighlightControlUnderMouse(rayCaster, assign) {
        let sortedPts = this.controlPoints.map(o => o);
        //search for arrow
        let cylinders = sortedPts.map(o => o.arrow.cylinder);

        let cones = sortedPts.map(o => o.arrow.arrow.cone);
        let intersection = rayCaster.intersectObjects(cylinders);
        let index = -1;
        if (intersection.length > 0)
            index = cylinders.indexOf(intersection[0].object);
        else {
            intersection = rayCaster.intersectObjects(cones);
            if (intersection.length > 0)
                index = cones.indexOf(intersection[0].object);
        }
        if (index != -1) {
            //highlight arrow
            if (index != -1) {
                this.highlightedTriangles = sortedPts[index].highlight();
                cylinders[index].material.color = new THREE.Color(0xFFA500);
                cones[index].material.color = new THREE.Color(0xFFA500);

                if (assign)
                    this.draggingPoint = sortedPts[index];
                return true;
            }
        }
        return false;
    }
    onMouseMove(mouse) {
        //clear highlight triangles
        removeFromScene(this.highlightedTriangles, this.viewer, this.sceneName);
        this.highlightedTriangles = null;

        if (!this.viewer.impl) return;

        let rayCaster = pointerToRaycaster(this.viewer, mouse);

        //reset arrows colors
        this.controlPoints.forEach(o => o.arrow.cylinder.material.color = new THREE.Color(o.color));
        this.controlPoints.forEach(o => o.arrow.arrow.cone.material.color = new THREE.Color(o.color));

        if (this.draggingPoint) {
            let currentMode = store.getters["getTransformMode"];
            let center = this.controlBox.clone().getCenter();
            let min = this.controlBox.min;
            let max = this.controlBox.max;
            let plane = null;
            if (this.draggingPoint.type == 'Top' || this.draggingPoint.type == 'Bottom')
                plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -center.y);
            else
                plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -center.z);
            let intersection = rayCaster.ray.intersectPlane(plane);
            if (!intersection)
                return;
            switch (this.draggingPoint.type) {
                case 'Front':
                    if (currentMode == "translate") {
                        let length = max.y - min.y;
                        if (intersection.y < max.y) min.y = intersection.y;
                        max.y = min.y + length;
                    } else {
                        if (intersection.y < max.y) min.y = intersection.y;
                    }
                    break;
                case 'Back':
                    if (currentMode == "translate") {
                        let length = max.y - min.y;
                        if (intersection.y > min.y) max.y = intersection.y;
                        min.y = max.y - length;
                    } else {
                        if (intersection.y > min.y) max.y = intersection.y;
                    }
                    break;
                case 'Left':
                    if (currentMode == "translate") {
                        let length = max.x - min.x;
                        if (intersection.x < max.x) min.x = intersection.x;
                        max.x = min.x + length;
                    } else {
                        if (intersection.x < max.x) min.x = intersection.x;
                    }
                    break;
                case 'Right':
                    if (currentMode == "translate") {
                        let length = max.x - min.x;
                        if (intersection.x > min.x) max.x = intersection.x;
                        min.x = max.x - length;
                    } else {
                        if (intersection.x > min.x) max.x = intersection.x;
                    }
                    break;
                case 'Bottom':
                    if (currentMode == "translate") {
                        let length = max.z - min.z;
                        if (intersection.z < max.z) min.z = intersection.z;
                        max.z = min.z + length;
                    } else {
                        if (intersection.z < max.z) min.z = intersection.z;
                    }
                    break;
                case 'Top':
                    if (currentMode == "translate") {
                        let length = max.z - min.z;
                        if (intersection.z > min.z) max.z = intersection.z;
                        min.z = max.z - length;
                    } else {
                        if (intersection.z > min.z) max.z = intersection.z;
                    }
                    break;
            }

            let newBox = new THREE.Box3(min, max);

            if (newBox.min.x > newBox.max.x || newBox.min.y > newBox.max.y || newBox.min.z > newBox.max.z) {
                newBox = this.controlBox;
            }

            this.controlBox = newBox;
            this.highlightedTriangles = this.draggingPoint.highlight();
            this.updateBoxControls();

            this.controlPoints.forEach(o => o.arrow.cylinder.material.needsUpdate = true);
            this.controlPoints.forEach(o => o.arrow.arrow.cone.material.needsUpdate = true);
            this.viewer.impl.invalidate(false, false, true);
        }
        else {
            this.checkAndHighlightControlUnderMouse(rayCaster, false);
        }
    }
    onMouseDown(mouse) {
        //clear highlight triangles
        removeFromScene(this.highlightedTriangles, this.viewer, this.sceneName);
        this.highlightedTriangles = null;

        if (!this.viewer.impl || mouse.which != 1) return;

        let rayCaster = pointerToRaycaster(this.viewer, mouse);
        if (this.checkAndHighlightControlUnderMouse(rayCaster, true)) {
            this.viewer.navigation.setIsLocked(true);
        }
    }
    onMouseUp(mouse) {
        if (!this.viewer.impl || mouse.which != 1) return;

        if (this.draggingPoint) {
            let currentMode = store.getters["getTransformMode"];
            let scaffold = store.getters['GET_EDITING_TAG'];
            let unitScale = (store.state.unitScale) ? store.state.unitScale : 1.0

            if (scaffold instanceof ForgeScaffold) {
                scaffold.viewPosition = this.controlBox.getCenter();
                if (currentMode == "scale") {
                    let min = this.controlBox.min;
                    let max = this.controlBox.max;

                    scaffold.length = (max.y - min.y) * unitScale;
                    scaffold.width = (max.x - min.x) * unitScale;
                    scaffold.height = (max.z - min.z) * unitScale;
                }

                store.state.forgeService.updateTagsIconsIn3D();
            }
        }

        this.draggingPoint = null;
        TurnViewerLockOff();
    }
}

Autodesk.Viewing.theExtensionManager.registerExtension('ScaffoldBoxExtension', ScaffoldBoxExtension);